import { useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@/app/container';
import { ContentRepositoryToken } from '../../di/tokens';
import type {
  ClassifyContentInput,
  LinkContentInput,
  SetContentClassificationInput,
} from '../../domain/entities/content.entity';

/**
 * Ações sobre as arestas de um content item (classificações ↔ subject e relações
 * ↔ conteúdo). Todas invalidam o detalhe do item para refletir o novo grafo.
 */
function useInvalidateItem(contentId: string) {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      queryKey: ['content', 'item', contentId],
    });
}

export function useClassifyContent(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItem(contentId);
  return useMutation({
    mutationFn: (input: ClassifyContentInput) =>
      repository.classifyContent(contentId, input),
    onSuccess: invalidate,
  });
}

/**
 * Reclassificar: repõe a classificação inteira do conteúdo.
 *
 * Invalida **também** o preview de prova, e não só o detalhe do item: quem
 * reclassifica hoje é o card da questão dentro do preview, e a classificação
 * que ele mostra vem no payload do preview (com o nome do assunto resolvido).
 * Sem essa segunda invalidação, o card continuaria exibindo o assunto antigo
 * até um F5.
 */
export function useSetContentClassification(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const queryClient = useQueryClient();
  const invalidateItem = useInvalidateItem(contentId);
  return useMutation({
    mutationFn: (input: SetContentClassificationInput) =>
      repository.setContentClassification(contentId, input),
    onSuccess: () => {
      invalidateItem();
      void queryClient.invalidateQueries({
        queryKey: ['content', 'exam-preview'],
      });
    },
  });
}

export function useSetSubjectRelation(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItem(contentId);
  return useMutation({
    mutationFn: (vars: { subjectId: string; relation: string }) =>
      repository.setSubjectRelation(contentId, vars.subjectId, vars.relation),
    onSuccess: invalidate,
  });
}

export function useUnclassifyContent(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItem(contentId);
  return useMutation({
    mutationFn: (subjectId: string) =>
      repository.unclassifyContent(contentId, subjectId),
    onSuccess: invalidate,
  });
}

export function useLinkContent(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItem(contentId);
  return useMutation({
    mutationFn: (input: LinkContentInput) =>
      repository.linkContent(contentId, input),
    onSuccess: invalidate,
  });
}

export function useUnlinkContent(contentId: string) {
  const repository = container.resolve(ContentRepositoryToken);
  const invalidate = useInvalidateItem(contentId);
  return useMutation({
    mutationFn: (vars: { targetContentId: string; relation: string }) =>
      repository.unlinkContent(contentId, vars.targetContentId, vars.relation),
    onSuccess: invalidate,
  });
}
